import { all, takeEvery, fork, call, put } from "redux-saga/effects";
import { reviewCrawlingSettingService } from "@iso/services";
import actions from "./actions";

const CONFLICT_ERROR_CODE = "REVIEW_CRAWLING_EXECUTION_CONFLICT";

export function* searchReviewCrawlingSettings() {
  yield takeEvery(actions.SEARCH_REQUEST, function* ({ payload }) {
    const { keyword, executionType, isActive, latestStatus, page, size, resolve, reject } =
      payload;
    try {
      const response = yield call(reviewCrawlingSettingService.search, {
        keyword,
        executionType,
        isActive,
        latestStatus,
        page,
        size,
      });
      resolve && resolve(response);
      const rawItems =
        response?.items ??
        response?.reviewCrawlingSettings ??
        (Array.isArray(response) ? response : null);
      const items = Array.isArray(rawItems) ? rawItems : [];
      const total = Number(response?.total);
      yield put({
        type: actions.SEARCH_SUCCESS,
        items,
        total: Number.isFinite(total) ? total : 0,
      });
    } catch (e) {
      yield put({ type: actions.SEARCH_ERROR, error: e });
      reject && reject(e);
    }
  });
}

function* createReviewCrawlingSetting() {
  yield takeEvery(actions.CREATE_REQUEST, function* ({ payload }) {
    const { data, resolve, reject } = payload;
    try {
      const response = yield call(reviewCrawlingSettingService.create, data);
      yield put({ type: actions.CREATE_SUCCESS });
      resolve(response);
    } catch (error) {
      yield put({ type: actions.CREATE_ERROR, error: error.response });
      reject(error);
    }
  });
}

function* updateReviewCrawlingSetting() {
  yield takeEvery(actions.UPDATE_REQUEST, function* ({ payload }) {
    const { data, resolve, reject } = payload;
    try {
      const response = yield call(reviewCrawlingSettingService.update, data);
      yield put({ type: actions.UPDATE_SUCCESS });
      resolve(response);
    } catch (error) {
      yield put({ type: actions.UPDATE_ERROR, error: error.response });
      reject(error);
    }
  });
}

export function* getReviewCrawlingSettingDetail() {
  yield takeEvery(actions.DETAIL_REQUEST, function* ({ payload }) {
    const { reviewCrawlingSettingId, resolve, reject } = payload;
    try {
      const response = yield call(reviewCrawlingSettingService.getDetail, {
        reviewCrawlingSettingId,
      });
      resolve && resolve(response);
      yield put({
        type: actions.DETAIL_SUCCESS,
        detail: response,
      });
    } catch (e) {
      yield put({ type: actions.DETAIL_ERROR });
      reject && reject(e);
    }
  });
}

function* deleteReviewCrawlingSetting() {
  yield takeEvery(actions.DELETE_REQUEST, function* ({ payload }) {
    const { reviewCrawlingSettingId, resolve, reject } = payload;
    try {
      const response = yield call(reviewCrawlingSettingService.delete, {
        reviewCrawlingSettingId,
      });
      yield put({ type: actions.DELETE_SUCCESS });
      resolve(response);
    } catch (error) {
      yield put({ type: actions.DELETE_ERROR });
      reject(error);
    }
  });
}

function* executeReviewCrawlingSetting() {
  yield takeEvery(actions.EXECUTE_REQUEST, function* ({ payload }) {
    const { reviewCrawlingSettingId, forceQueue, resolve, reject } = payload;
    try {
      const response = yield call(reviewCrawlingSettingService.execute, {
        reviewCrawlingSettingId,
        forceQueue,
      });
      yield put({ type: actions.EXECUTE_SUCCESS });
      resolve(response);
    } catch (error) {
      yield put({ type: actions.EXECUTE_ERROR });
      const conflict = error?.response?.data;
      if (conflict?.code === CONFLICT_ERROR_CODE) {
        reject({
          conflict: true,
          conflictingId: conflict.conflictingId,
          conflictingTitle: conflict.conflictingTitle,
        });
      } else {
        reject(error);
      }
    }
  });
}

function* cancelReviewCrawlingSetting() {
  yield takeEvery(actions.CANCEL_REQUEST, function* ({ payload }) {
    const { reviewCrawlingSettingId, resolve, reject } = payload;
    try {
      const response = yield call(reviewCrawlingSettingService.cancel, {
        reviewCrawlingSettingId,
      });
      yield put({ type: actions.CANCEL_SUCCESS });
      resolve(response);
    } catch (error) {
      yield put({ type: actions.CANCEL_ERROR });
      reject(error);
    }
  });
}

function* getCurrentJob() {
  yield takeEvery(actions.GET_CURRENT_REQUEST, function* ({ payload }) {
    const { reviewCrawlingSettingId } = payload;
    try {
      const response = yield call(reviewCrawlingSettingService.getCurrent, {
        reviewCrawlingSettingId,
      });
      yield put({
        type: actions.GET_CURRENT_SUCCESS,
        currentJob: response,
      });
    } catch (error) {
      yield put({ type: actions.GET_CURRENT_ERROR });
    }
  });
}

function* getHistories() {
  yield takeEvery(actions.GET_HISTORIES_REQUEST, function* ({ payload }) {
    const { reviewCrawlingSettingId } = payload;
    try {
      const response = yield call(reviewCrawlingSettingService.getRuns, {
        reviewCrawlingSettingId,
      });
      const raw = response?.runs ?? response?.histories ?? response ?? [];
      yield put({
        type: actions.GET_HISTORIES_SUCCESS,
        histories: Array.isArray(raw) ? raw : [],
      });
    } catch (error) {
      yield put({ type: actions.GET_HISTORIES_ERROR });
    }
  });
}

function* validateSettingFile() {
  yield takeEvery(actions.VALIDATE_SETTING_FILE_REQUEST, function* ({ payload }) {
    const { file, resolve, reject } = payload;
    try {
      const response = yield call(reviewCrawlingSettingService.validateSettingFile, file);
      yield put({ type: actions.VALIDATE_SETTING_FILE_SUCCESS });
      resolve && resolve(response);
    } catch (error) {
      yield put({ type: actions.VALIDATE_SETTING_FILE_ERROR, error: error.response });
      reject && reject(error);
    }
  });
}

function* importExcel() {
  yield takeEvery(actions.IMPORT_REQUEST, function* ({ payload }) {
    const { reviewCrawlingSettingId, file, resolve, reject } = payload;
    try {
      const response = yield call(reviewCrawlingSettingService.importExcel, {
        reviewCrawlingSettingId,
        file,
      });
      yield put({ type: actions.IMPORT_SUCCESS });
      resolve(response);
    } catch (error) {
      yield put({ type: actions.IMPORT_ERROR });
      reject(error);
    }
  });
}

export default function* rootSaga() {
  yield all([
    fork(searchReviewCrawlingSettings),
    fork(createReviewCrawlingSetting),
    fork(updateReviewCrawlingSetting),
    fork(getReviewCrawlingSettingDetail),
    fork(deleteReviewCrawlingSetting),
    fork(executeReviewCrawlingSetting),
    fork(cancelReviewCrawlingSetting),
    fork(getCurrentJob),
    fork(getHistories),
    fork(importExcel),
    fork(validateSettingFile),
  ]);
}
